import { useTexture } from "@react-three/drei";
import React, { useEffect, useMemo, useRef } from "react";
import { Matrix4, Vector3, Euler, Quaternion, InstancedMesh } from "three";
import { degToRad } from "three/src/math/MathUtils";

interface InstancedSphereProps {
  count: number;
  radius: number;
  positions: Array<[number, number, number]>;
  rotations: Array<[number, number, number]>;
  scales: Array<number>;
}

const InstancedSpheres = React.memo(
  ({
    count = 10,
    radius = 1,
    positions,
    rotations,
    scales,
  }: InstancedSphereProps) => {
    const matrixRef = useRef(null);

    const instancedRef = useRef<InstancedMesh>();
    const [texture, matcap] = useTexture([
      "/balltexture.jpg",
      "/matcap_base_3.png",
    ]);

    useMemo(() => {
      const tempMatrix = new Matrix4();
      const tempPosition = new Vector3();
      const tempRotation = new Euler();
      const tempScale = new Vector3();
      const tempQuaternion = new Quaternion();

      matrixRef.current = Array.from({ length: count }, (_, i) => {
        const posX = positions[i]?.[0] || 0;
        const posY = positions[i]?.[1] || 0;
        const posZ = positions[i]?.[2] || 0;

        const rotX = rotations[i]?.[0] || 0;
        const rotY = rotations[i]?.[1] || 0;
        const rotZ = rotations[i]?.[2] || 0;

        const scale = scales[i] || 1;

        tempPosition.set(posX, posY, posZ);
        tempRotation.set(degToRad(rotX), degToRad(rotY), degToRad(rotZ));
        tempScale.set(scale, scale, scale);

        tempQuaternion.setFromEuler(tempRotation);

        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
        return tempMatrix.clone();
      });
    }, [count, positions, rotations, scales]);

    useEffect(() => {
      if (instancedRef.current && matrixRef.current) {
        matrixRef.current.forEach((matrix, i) => {
          instancedRef.current.setMatrixAt(i, matrix);
        });
        instancedRef.current.instanceMatrix.needsUpdate = true;
      }
    }, [matrixRef]);

    return (
      <instancedMesh ref={instancedRef} args={[null, null, count]}>
        <sphereGeometry args={[radius, 32, 32]} />
        <meshMatcapMaterial map={texture} matcap={matcap} color={"#fdc322"} />
      </instancedMesh>
    );
  }
);

InstancedSpheres.displayName = "InstancedSpheres";

export default InstancedSpheres;
